import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';
import { Button, Spinner } from '@hero/web-components';
import { useMapContext } from '@hero/web-context';
import { cn } from '@hero/web-lib';

/**
 * MapSaveButton - Renders Save Changes and Cancel buttons
 * Uses context to access state and handlers. Loading state matches ModalForm (Spinner + "Saving...").
 */
export function MapSaveButton() {
  const ctx = useMapContext();
  const { t, i18n } = useTranslation('c-main');
  const isRTL = i18n.language === 'ar';
  const saving = Boolean(ctx.saveInProgress);
  // Pre-save work in flight (e.g. quoting the new lane). Saving is blocked
  // until it lands so the user can't outrun the answer.
  const preparing = Boolean(ctx.savePreparing) && !saving;

  /**
   * Name the wait. A bare "Loading..." on a disabled Save button reads like
   * the map is stuck; "Checking price..." tells the user their pin moved to
   * another city and a quote is on its way, which is also why they're about
   * to be asked to pick a price.
   */
  const busyLabel = saving
    ? t('async_operations.saving')
    : ctx.savePreparingReason === 'price_check'
    ? t('async_operations.checking_price')
    : t('async_operations.loading');

  if (!ctx.enableConfirmLocationsButton || !ctx.hasUnsavedChanges || ctx.editingIndex !== null) {
    return null;
  }

  return (
    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30">
      <div className="flex items-center gap-3 bg-white/20 backdrop-blur-md rounded-lg shadow-sm border border-gray-200/50 p-2.5">
        <Button
          variant="ghost"
          onClick={ctx.handleCancel}
          disabled={saving}
          className="h-12 px-6 text-base font-medium text-gray-700 hover:bg-white/30 hover:text-gray-900 min-w-[130px] flex items-center justify-center gap-2 transition-colors">
          <X className="w-5 h-5" />
          {t('crud_buttons.cancel')}
        </Button>
        <Button
          onClick={ctx.handleSaveChanges}
          disabled={saving || preparing}
          className="h-12 bg-primary hover:bg-primary/90 text-white px-7 text-base font-semibold min-w-[160px] transition-all duration-200 hover:shadow-lg rounded-md flex items-center justify-center gap-2">
          {saving || preparing ? (
            <>
              <Spinner className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
              <span>{busyLabel}</span>
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              {t('crud_buttons.save')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
